import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  alpha,
  useTheme,
} from '@mui/material';
import { Shipment } from '../types';
import API from '@Services/apiAxios';
import SbButton from '@Components/Button';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@Store';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';
import dayjs, { Dayjs } from 'dayjs';
import DatePicker from '@Components/DatePicker';
import SbTextField from '@Components/Textfield';
import { SBTable } from '@Components/Table/Table';
import { TableHeadCell, TableRowCell } from '@Components/Table/TableCell';
import { SBTableRow } from '@Components/Table/TableRow';

interface Props {
  carrierId: string;
  selectedShipments: Shipment[];
}

const Step3FinalSubmit: React.FC<Props> = ({ carrierId, selectedShipments }) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const [invoiceDate, setInvoiceDate] = useState<Dayjs | null>(dayjs());
  const [dueDate, setDueDate] = useState<Dayjs | null>(dayjs());
  const [carrierRef, setCarrierRef] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        carrierId,
        billDate: invoiceDate,
        dueDate,
        carrierReferenceNumber: carrierRef,
        purchaseInvoiceNumber: selectedShipments?.[0]?.purchaseInvoiceNumber,
        shipmentNumbers: selectedShipments.map(s => s.shipmentNumber),
      };
      await API.post('/invoice/purchase-invoice/create-credit-note', payload);
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: 'Success',
          subTitle: 'Credit note created successfully',
          navigateTo: '/purchase-invoices',
        }),
      );
    } catch (error) {
      console.error('Submission failed:', error);
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: error?.response?.data?.msg,
          subTitle: error?.response?.data?.errorDetails || 'Something went to wrong.',
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Side-by-side Layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          gap: '5rem',
        }}
      >
        {/* Left side — Invoice Details */}
        <Grid item xs={12} md={6}>
          {/* Dates Row */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Invoice Date"
                value={invoiceDate}
                handleChange={newValue => setInvoiceDate(newValue)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                minDate={dayjs(invoiceDate)}
                label="Due Date"
                value={dueDate}
                handleChange={newValue => setDueDate(newValue)}
                required
              />
            </Grid>
          </Grid>

          {/* Reference No */}
          <Grid container sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <SbTextField
                placeholder="Enter Carrier Reference Number"
                label="Carrier Reference Number"
                name="altContactName"
                onChange={e => setCarrierRef(e.target.value)}
                value={carrierRef}
                required
              />
            </Grid>
          </Grid>

          <SbButton
            variant="contained"
            size="large"
            disabled={!invoiceDate || !dueDate || !carrierRef || isSubmitting}
            onClick={handleSubmit}
            sx={{
              color: 'primary',
              mt: 3,
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Send to 365'}
          </SbButton>
          {/* Info Message */}
          {(!invoiceDate || !dueDate || !carrierRef) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Please fill all the required fields to submit the invoice.
            </Alert>
          )}
        </Grid>

        {/* Right side — Shipments Table */}
        <Grid item xs={12} md={6}>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              overflowY: 'auto',
              maxHeight: '410px',
            }}
          >
            <SBTable size="medium">
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableHeadCell sx={{ fontWeight: '600', fontSize: '0.95rem' }}>Shipment Number</TableHeadCell>
                  <TableHeadCell sx={{ fontWeight: '600', fontSize: '0.95rem' }}>Origin → Destination</TableHeadCell>
                  <TableHeadCell sx={{ fontWeight: '600', fontSize: '0.95rem' }}>Amount</TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedShipments.map((shipment, index) => (
                  <SBTableRow
                    key={shipment.shipmentNumber}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      bgcolor: index % 2 === 0 ? 'white' : 'grey.50',
                    }}
                  >
                    <TableRowCell align="center" className="md-text capitalize">
                      {shipment.shipmentNumber}
                    </TableRowCell>
                    <TableRowCell align="center" className="md-text capitalize">
                      <Box>
                        <Typography>{shipment.origin}</Typography>
                        <Typography>→ {shipment.destination}</Typography>
                      </Box>
                    </TableRowCell>
                    <TableRowCell align="center" className="md-text capitalize">
                      {shipment.currency} {shipment.amount.toLocaleString('en-IN')}
                    </TableRowCell>
                  </SBTableRow>
                ))}
              </TableBody>
            </SBTable>
          </TableContainer>
        </Grid>
      </Box>

      {/* Submit Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SbButton
          variant="contained"
          size="large"
          disabled={!invoiceDate || !dueDate || !carrierRef || isSubmitting}
          onClick={handleSubmit}
          sx={{
            color: 'primary',
          }}
        >
          {isSubmitting ? 'Creating...' : 'Create Invoice'}
        </SbButton>
      </Box>
    </Box>
  );
};

export default Step3FinalSubmit;
